/* global Modernizr, TurbolinksAnimate */

import $ from 'cash-dom';
import { contain } from 'intrinsic-scale';
import { easeInOutQuad } from 'es6-easings';
import hoverintent from 'hoverintent';
import objectFitImages from 'object-fit-images';
import PubSub from 'pubsub-js';
import Packery from 'packery';
// import ScrollReveal from 'scrollreveal';
import { slideUp, slideDown } from 'es6-slide-up-down';
import Swiper from 'swiper';
import trim from 'lodash/trim';
import Turbolinks from 'turbolinks';
import 'turbolinks-animate';
import zenscroll from 'zenscroll';
import 'what-input';

import { cover } from './cover-contain';
import detectGridRows from './detect-grid';
import MQHelper from './mq-helper';
import parseQueryString from './parse-query-string';
import './once';
import './resizestartstop';

const mq = new MQHelper({ watch: true });

Turbolinks.start();

const selectors = {
  document,
  window,

  html: 'html',
  body: 'body',

  header: '.js-header',
  navToggle: '.js-nav-toggle',
  anchorLinks: 'a[href^="#"]',

  slideshow: '.swiper-container',
  syncedSlideshow: '.swiper-container--sync',
  slideshowCaptionDisplay: '.js-slideshow-caption-display',
  slideshowPrev: '.js-slideshow-prev',
  slideshowNext: '.js-slideshow-next',

  projectGrid: '.js-project-grid',
  projectGridSizer: '.js-project-grid-sizer',
  projectGridGutter: '.js-project-grid-gutter',
  projectGridItem: '.js-project-grid-item',
  projectGridFilterItem: '.js-project-grid-filter-item',
  projectPreview: '.js-project-preview',

  project: '.js-project',
  projectOverlay: '.js-project-overlay',
  projectOverlayToggle: '.js-project-overlay-toggle',
  projectOverlayClose: '.js-project-overlay-close',
  projectScrollContent: '.js-project-scroll-content',

  projectSlideFigure: '.js-project-slide-figure',

  imageCreditsContainer: '.js-image-credits-container',

  accordion: '.js-accordion',
  accordionItem: '.js-accordion-item',
  accordionToggle: '.js-accordion-toggle',
  accordionContent: '.js-accordion-content',

  fittedMedia: 'iframe[data-background=true]',
};

const config = {
  smoothScrollingSpeed: 600,
  objectFitInterval: 3000,
  transitionDuration: 0,
  breakpoints: {},
};

export default class App {

  constructor(_config = {}) {

    // Merge defaults and passed config object
    this.config = {
      ...config,
      ..._config,
    };

    // Read shared config from CSS
    this.readCSSConfig();

    // On DOM loaded: init all events and sub-modules
    $(this.init.bind(this));
  }

  init() {
    this.headerHeight = 0;

    // Add breakpoints to MQ helper
    mq.addQueries(this.config.breakpoints);

    // Cache elements for later use
    this.cacheElements();

    // Pub/sub and event bindings
    this.setupEvents();

    // Polyfills
    this.polyfillObjectFit();

    // Modules
    this.setupTurboLinks();
    this.setupSmoothAnchorScrolling();
    this.setupExternalLinks();
    this.setupFittedMedia();
    this.markBodyDuringResize();
    this.measureHeaderSize();
    this.setupNavigation();
    this.setupImageCredits();
    this.setupSlideshows();
    this.setupAccordions();
    this.setupProjectGrid();
    this.setupProject();

    // Mark page as loaded
    this.$e.html.removeClass('preload');
  }

  // Fetch elements for all defined selectors

  cacheElements() {
    this.selectors = selectors;
    this.elements = Object.assign({}, ...Object.keys(this.selectors).map(k => ({ [k]: $(k) })));
    this.$e = this.elements;
  }

  // Events and pub/sub for interaction with other modules

  setupEvents() {
    // Update CSS config on mediaquery change
    PubSub.subscribe('mediaquery.change', this.readCSSConfig.bind(this));

    // Custom event on mediaquery change
    PubSub.subscribe('mediaquery.change', (/* event, mq */) => {
      console.log('Breakpoint', mq.current);
    });

    // Ajax behaviour and state changes
    $(document).on('beforecache', this.beforeDomCache.bind(this));
    $(document).on('statechange', this.onStateChange.bind(this));

    this.scrollToCurrentHash();
  }

  // Configure turbolinks

  setupTurboLinks() {

    // Move turbolinks events into general namespace

    $(document).on('turbolinks:before-cache', () => {
      $(document).trigger('beforecache');
    });
    $(document).on('turbolinks:load', () => {
      $(document).trigger('statechange');
    });

    // Disable for visits into the admin area
    const regexAdmin = /^(?:\w+:\/\/)?([^/]+)\/(admin|processwire)\b/i;
    $(document).on('turbolinks:click', (event) => {
      if (event.data.url.match(regexAdmin)) {
        event.preventDefault();
      }
    });

    // Enable page transitions

    $(document).on('turbolinks:load', () => {
      TurbolinksAnimate.init({ animation: 'fadein' });
    });

    // Pre-fetch links on hover

    $(document).on('turbolinks:load', () => {
      this.prefetchInternalLinks();
    });
    this.prefetchInternalLinks();
  }

  // Prefetch internal links on hover

  prefetchInternalLinks() {
    let $prefetcher;

    $('a[href^="/"]:not([data-turbolinks=false])')
      .once('prefetch')
      .each((index, node) => {
        hoverintent(node, () => {
          const href = node.getAttribute('href');
          if ($prefetcher) {
            if ($prefetcher.attr('href') !== href) {
              $prefetcher.attr('href', href);
            }
          }
          else {
            $prefetcher = $('<link>').attr('rel', 'prefetch').attr('href', href).appendTo('header');
          }
        }, () => {});
      });
  }

  beforeDomCache() {
    $('.lazyloaded').removeClass('.lazyloaded');
    $('.lazyloading').removeClass('.lazyloading');
  }

  // Handler that executes after every page load (http or ajax)

  onStateChange() {
    this.trackPageView();
  }

  // Check if page has template (body class name: template-xxx)

  pageHasTemplate(template, element = null) {
    const templates = Array.isArray(template) ? template : [template];
    const selector = templates.map(t => `.template-${t}`).join(',');
    return $(element || 'body').is(selector);
  }

  // Read and initialize all shared CSS config values

  readCSSConfig() {
    // Default transition duration in ms
    this.config.transitionDuration = this.getCSSConfigValue('transition') || 0;

    // Breakpoints / media queries
    if (Object.keys(this.config.breakpoints).length === 0) {
      const cssBreakpoints = this.getCSSConfigValue('breakpoints');
      this.config.breakpoints = parseQueryString(cssBreakpoints);
    }

    PubSub.publish('css.readconfig');
  }

  // Read shared config from CSS via <meta> tag's font-family

  getCSSConfigValue(key) {
    let configStr = $(`.js-css-config[data-key="${key}"]`).css('font-family') || '';
    configStr = trim(configStr.trim(), '"\''); // browsers re-quote string styles
    return configStr;
  }

  // Polyfill object-fit for images, if necessary

  polyfillObjectFit() {
    if (Modernizr.objectfit) return;

    const triggerObjectFit = () => {
      objectFitImages(null, { watchMQ: true });
    };

    triggerObjectFit();
    setTimeout(triggerObjectFit, config.objectFitInterval / 2);
    setInterval(triggerObjectFit, config.objectFitInterval);
  }

  // Track page view

  trackPageView(location = null) {
    if (typeof(_etracker) === 'object') {
      const accountKey = $('meta[name="x-etracker-key"]').attr('content');
      const pageName = $('meta[name="x-page-name"]').attr('content');
      const pageArea = $('meta[name="x-page-area"]').attr('content');
      const pageUrl = location || (window.location.pathname + window.location.search);
      et_eC_Wrapper({ et_et: accountKey, et_pagename: pageName, et_areas: pageArea, et_url: pageUrl });
    }
  }

  // Track custom Google Analytics events

  trackCustomEvent(category, action, label) {
    if (typeof window.ga === 'function') {
      window.ga('send', 'event', category, action, label);
    }
  }

  // Add body class while resizing window

  markBodyDuringResize() {
    $(window).on('resizestart', () => {
      this.$e.html.addClass('is-resizing');
    });
    $(window).on('resizestop', () => {
      this.$e.html.removeClass('is-resizing');
    });
  }

  // Measure header size and save to variable

  measureHeaderSize() {
    const calculate = () => {
      const $header = $(selectors.header);
      this.headerHeight = Math.ceil($header.outerHeight());
      document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
    };

    $(document).on('statechange', calculate);
    $(window).on('resizestop', calculate);
    calculate();
  }

  // Open external links in new tab

  setupExternalLinks() {
    const processLinks = () => {
      $('a')
        .once('external')
        .filter('[href^="http"], [href^="//"]')
        .not(`[href*="${window.location.host}"]`)
        .attr('rel', 'noopener noreferrer')
        .attr('target', '_blank');
    };

    $(document).on('statechange', processLinks);
    processLinks();
  }

  // Scroll to hash / anchor

  getScroller(element = null, duration = null, offset = 0) {
    const defaultDuration = duration !== null ? duration : this.config.smoothScrollingSpeed;
    const scrollElem = element || document.scrollingElement || document.body;
    const calcOffset = offset + this.headerHeight;
    return zenscroll.createScroller(scrollElem, defaultDuration, calcOffset);
  }

  scrollTo(hash) {
    const anchor = hash.replace('#', '');

    let $target = $(`#${anchor}`);
    $target = $target.length ? $target : $(`#anchor__${anchor}`);
    $target = $target.length ? $target : $(`[name="${anchor}"]`);

    if ($target.length) {
      this.getScroller().to($target.get(0), this.config.smoothScrollingSpeed);
      return true;
    }

    return false;
  }

  // Scroll to current hash

  scrollToCurrentHash() {
    const hash = window.location.hash.replace('#', '');
    if (hash) this.scrollTo(hash);
  }

  // Make anchor links scroll smoothly

  setupSmoothAnchorScrolling() {
    zenscroll.setup(this.config.smoothScrollingSpeed, 0);

    $(document).on('click', this.selectors.anchorLinks, (event) => {
      if (!window.noZensmooth) return;
      const $anchor = $(event.target).closest(this.selectors.anchorLinks);
      if ($anchor.data('no-scroll')) {
        event.preventDefault();
        return;
      }
      const href = $anchor.get(0).hash;
      if (this.scrollTo(href)) {
        event.preventDefault();
      }
    });
  }

  // Toggle navigation on button click

  setupNavigation() {
    $(document).on('click', this.selectors.navToggle, (event) => {
      $('body').toggleClass('is-nav-visible');
      event.preventDefault();
    });

    const process = () => {
      if (this.pageHasTemplate('home')) {
        $('body').addClass('is-nav-visible');
      }
    };

    $(document).on('statechange', process);
    process();
  }

  // Slideshows

  setupSlideshows() {
    const process = () => {
      const $slideshows = $(selectors.slideshow).not(selectors.syncedSlideshow).once('slideshow');
      $slideshows.each((i, slideshow) => {
        const $slideshow = $(slideshow);
        const speed = $slideshow.data('speed') || 600;
        const effect = $slideshow.data('effect') || 'slide';
        const controls = $slideshow.data('controls');
        const count = $slideshow.find('.swiper-slide').length;
        const hasMultipleSlides = count > 1;
        const allowTouchMove = hasMultipleSlides;
        let autoplay = false;
        let syncedSwiper;
        let space = 0;

        if ($slideshow.is('[data-autoplay]') && !$slideshow.is('[data-autoplay="false"]')) {
          const delay = parseInt($slideshow.data('autoplay'), 10);
          autoplay = delay ? { delay } : true;
        }

        if ($slideshow.is('[data-space]')) {
          space = 18;
        }

        const swiperOptions = {
          speed,
          autoplay,
          effect,
          allowTouchMove,
          loop: true,
          grabCursor: true,
          init: false,
          spaceBetween: space || 0,
          navigation: {
            prevEl: selectors.slideshowPrev,
            nextEl: selectors.slideshowNext,
          },
          keyboard: {
            enabled: true,
            onlyInViewport: true,
          },
        };

        const $syncedSlideshow = $(`[data-controlled-by="${controls}"]`);
        if ($syncedSlideshow.length) {
          console.log('Synced', $syncedSlideshow.get(0));
          syncedSwiper = new Swiper($syncedSlideshow.get(0), {
            ...swiperOptions,
            keyboard: false,
            grabCursor: false,
            allowTouchMove: false,
          });
          syncedSwiper.init();
        }

        const swiper = new Swiper(slideshow, {
          ...swiperOptions,
          controller: syncedSwiper ? {
            control: syncedSwiper,
          } : false,
        });

        // Fix lazyload images in duplicated slides
        const fixLazyloadMedia = () => {
          const images = Array.from(slideshow.querySelectorAll('.slideshow-lazyload'));
          images.forEach((img) => {
            img.classList.remove('slideshow-lazyload');
            img.classList.remove('lazyloading');
            img.classList.add('lazyload');
          });
        };
        swiper.on('init', fixLazyloadMedia);

        // Switch out captions
        const $caption = $(selectors.slideshowCaptionDisplay);
        const updateCaption = () => {
          const slide = swiper.slides[swiper.activeIndex];
          const caption = $(slide).attr('data-caption');
          $caption.text(caption || ' ');
        };
        if ($caption.length) {
          swiper.on('slideChange', updateCaption);
        }

        swiper.init();
        $slideshow.data('swiper', swiper);

        updateCaption();
      });
    };

    $(document).on('statechange', process);
    process();
  }

  // Accordions

  setupAccordions() {
    const duration = this.config.smoothScrollingSpeed;
    const easing = easeInOutQuad;

    const contentHeight = els => els.reduce((height, el) => height + el.offsetHeight, 0);

    const isOpen = e => e.is('[aria-current="step"]');
    const setOpen = (e, toggle = true) => {
      const $contents = e.children(selectors.accordionContent);

      // Determine speed in relation to doc height to allow smoother slide open animation
      let speed = 1; // factor
      if (toggle) {
        const content = $contents.get(0);
        speed = content.dataset.relativeHeight || 1;
        speed = Math.max(0.75, Math.min(3, 0));
      }

      // Set aria-attr
      e.attr('aria-current', toggle ? 'step' : false);
      // Slide content up/down
      $contents.each((i, content) => {
        (toggle ? slideDown : slideUp)(content, { duration: duration * speed, easing });
      });

      // Scroll to opened content (on mobile)
      if (toggle && !mq.is('medium')) {
        setTimeout(() => {
          this.getScroller().to(e.get(0), this.config.smoothScrollingSpeed);
        }, duration * speed + duration * 0.2);
      }
    };

    const process = () => {
      const $accordions = $(selectors.accordion).once('accordion');
      const hash = window.location.hash.replace('#', '');

      $accordions.each((i, accordion) => {
        const $accordion = $(accordion);
        const $items = $accordion.children(selectors.accordionItem);
        const $contents = $accordion.find(selectors.accordionContent);

        // Store relation to doc height to allow smoother slide open animation
        $contents.each((_, content) => {
          const relativeHeight = contentHeight([...content.children]) / window.innerHeight;
          content.dataset.relativeHeight = relativeHeight;
        });

        setOpen($items, false);
        setTimeout(() => {
          $accordion.attr('data-setup', 'true');
        }, duration);

        $items.each((i, item) => {
          const $item = $(item);
          const $toggle = $item.children(selectors.accordionToggle);
          const name = $item.data('name');
          $toggle.on('click', () => {
            if (isOpen($item)) {
              setOpen($item, false);
            }
            else {
              setOpen($items.not($item), false);
              setOpen($item, true);
            }
          });
          // Open if loaded via url #hash
          if (hash && hash === name) {
            const toggle = $toggle.get(0);
            if (!toggle) return;

            setTimeout(() => {
              const position = toggle.getBoundingClientRect().top / window.innerHeight;
              $toggle.trigger('click');
              if (position > 0.5) {
                setTimeout(() => {
                  this.getScroller().to(toggle);
                }, duration);
              }
            }, duration);
          }
        });
      });
    };

    $(document).on('statechange', process);
    process();
  }

  // Figure captions

  setupImageCredits() {
    const resize = () => {
      const $figures = $(selectors.imageCreditsContainer);
      $figures.each((_, el) => {
        const figure = el.querySelector('figure') || el;
        const $img = $(figure).find('img');
        if ($img.length) {
          const container = {
            x: $img.outerWidth(),
            y: $img.outerHeight(),
          };
          const inner = {
            x: Number($img.attr('width')),
            y: Number($img.attr('height')),
          };
          const { width, height } = contain(container.x, container.y, inner.x, inner.y);
          el.style.setProperty('--container-width', `${container.x}px`);
          el.style.setProperty('--container-height', `${container.y}px`);
          el.style.setProperty('--fitted-width', `${width}px`);
          el.style.setProperty('--fitted-height', `${height}px`);
        }
      });
    };

    $(document).on('statechange', resize);
    $(window).on('resizestop', resize);
    resize();
  }

  // Project

  setupProject() {
    const process = () => {
      const $project = $(selectors.project).once('project');
      if (!$project.length) return;

      const $overlayToggles = $project.find(selectors.projectOverlayToggle);
      const $overlayClose = $project.find(selectors.projectOverlayClose);
      const $overlays = $project.find(selectors.projectOverlay);
      const $content = $project.find(selectors.projectScrollContent);

      $overlayToggles.data('no-scroll', true);
      $overlayToggles.on('click', (event) => {
        const $overlay = $($(event.target).attr('href'));
        $overlay.toggleClass('is-visible');
        if ($overlay.hasClass('is-visible')) {
          $overlays.not($overlay).removeClass('is-visible');
        }
      });
      $overlayClose.on('click', () => {
        $overlays.removeClass('is-visible');
      });
      $content.on('click', () => {
        $overlays.removeClass('is-visible');
      });
    };

    const resize = () => {
      const $figures = $(selectors.projectSlideFigure);
      $figures.each((_, figure) => {
        const $img = $(figure).find('img');
        if ($img.length) {
          const container = {
            x: $img.outerWidth(),
            y: $img.outerHeight(),
          };
          const inner = {
            x: Number($img.attr('width')),
            y: Number($img.attr('height')),
          };
          const { width, height } = contain(container.x, container.y, inner.x, inner.y);
          figure.style.setProperty('--container-width', `${container.x}px`);
          figure.style.setProperty('--container-height', `${container.y}px`);
          figure.style.setProperty('--fitted-width', `${width}px`);
          figure.style.setProperty('--fitted-height', `${height}px`);
        }
      });
    };

    $(document).on('statechange', process);
    $(document).on('statechange', resize);
    $(window).on('resizestop', resize);
    process();
    resize();
  }

  // Project grid

  setupProjectGrid() {
    const isHidden = e => !(e.offsetWidth || e.offsetHeight || e.getClientRects().length);
    const isSetup = grid => !!grid.__packery;

    const setup = ($grid) => {
      const grid = $grid.get(0);

      if (isHidden(grid)) {
        return;
      }
      if (isSetup(grid)) {
        return;
      }

      const pack = new Packery(grid, {
        itemSelector: selectors.projectGridItem,
        columnWidth: selectors.projectGridSizer,
        gutter: selectors.projectGridGutter,
        percentPosition: true,
        initLayout: false,
      });

      grid.__packery = pack;

      let layoutInitialized = false;

      pack.on('layoutComplete', () => {
        if (layoutInitialized) {
          return;
        }

        $grid.attr('data-setup', 'true');

        // Sort images into rows and columns, then flatten again
        // so they can be revealed in the visually correct order
        const rows = detectGridRows(grid, { matching: selectors.projectPreview });
        const items = rows.flat(2);

        // ScrollReveal().reveal(items, {
        //   useDelay: 'onload',
        //   delay: 1000,
        //   duration: 750,
        //   interval: 120,
        //   viewOffset: {
        //     bottom: 50,
        //   },
        // });

        layoutInitialized = true;
      });

      pack.layout();

      // Filters

      const $items = $grid.find(selectors.projectGridItem).filter('[data-filter-values]');
      const $filters = $grid.find(selectors.projectGridFilterItem);
      $filters.on('click', (event) => {
        const $filter = $(event.target);
        const filterValue = String($filter.data('filter-value'));

        $filters.removeClass('is-active');
        $filter.addClass('is-active');

        if (filterValue) {
          const $visibleItems = $items.filter((_, item) => {
            const values = String($(item).data('filter-values'));
            const valueArray = values.split('|');
            return valueArray.indexOf(filterValue) > -1;
          });
          $items.not($visibleItems).addClass('is-hidden');
          $visibleItems.removeClass('is-hidden');
        }
        else {
          $items.removeClass('is-hidden');
        }
        pack.layout();
      });
    };

    const process = () => {
      const $grids = $(selectors.projectGrid);

      $grids.each((index, grid) => {
        const $grid = $(grid);
        setup($grid);

        const onResize = () => setup($grid);

        $(window).on('resizestop', onResize);
        $(document).on('beforecache', () => {
          $(window).off('resizestop', onResize);
        });
      });
    };

    $(document).on('statechange', process);
    process();
  }

  setupFittedMedia() {
    $(window).on('resizestop', 10, this.positionFittedMedia.bind(this));
    this.positionFittedMedia();
  }

  positionFittedMedia() {
    const $medias = $(selectors.fittedMedia);
    $medias.each((i, node) => {
      const $frame = $(node);
      const frameWidth = Number($frame.attr('width'));
      const frameHeight = Number($frame.attr('height'));

      const $container = $frame.parent();
      const $media = $container.find('img');
      const mediaWidth = $media.outerWidth(); // $container.outerWidth()
      const mediaHeight = $media.outerHeight(); // $container.outerHeight()

      const constraints = cover(
          mediaWidth,
          mediaHeight,
          frameWidth,
          frameHeight,
      );

      node.style.setProperty('--fitted-media-scale-x', constraints.width);
      node.style.setProperty('--fitted-media-scale-y', constraints.height);
    });
  }
}
